import {
  Checkbox,
  FormControl,
  FormHelperText,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  SelectChangeEvent,
  SxProps,
  Theme,
} from '@mui/material';
import { useField } from 'formik';
import { Option } from 'src/types/registrationFormTypes';

export interface CustomSelectProps {
  name: string;
  label: string;
  options: Option[];
  value?: string;
  selectComponentStyles?: SxProps<Theme>;
  isReadOnly?: boolean;
  alertMessage?: string;
  isMultipleSelect?: boolean;
  onValueChange?: (value: string | string[]) => void;
}

export const CustomSelect: React.FC<CustomSelectProps> = ({
  name,
  label,
  options,
  selectComponentStyles,
  isReadOnly = false,
  alertMessage,
  isMultipleSelect = false,
  onValueChange,
}) => {
  const [field, meta] = useField(name);
  const showError = meta.touched && meta.error;

  const getOptionName = (value: string) => {
    const isIdComparison = name === 'country' || name === 'preferredLanguage';
    const selectedOption = options.find((option) =>
      isIdComparison ? option.id === value : option.name === value,
    );

    return selectedOption ? selectedOption.name : value;
  };

  return (
    <FormControl
      variant="outlined"
      fullWidth
      margin="normal"
      data-field-name={name}
      sx={{ marginTop: '8px' }}
    >
      <InputLabel id={`${name}-label`} error={meta.touched && !!meta.error}>
        {label}
      </InputLabel>
      <Select
        {...field}
        labelId={`${name}-label`}
        label={label}
        value={field.value || ''}
        error={meta.touched && !!meta.error}
        onChange={(event: SelectChangeEvent<typeof field.value>) => {
          field.onChange(event);

          if (onValueChange) {
            onValueChange(event.target.value as any);
          }
        }}
        onBlur={field.onBlur}
        renderValue={(selectedValue) => {
          if (isMultipleSelect && Array.isArray(selectedValue)) {
            return selectedValue.map(getOptionName).join(', ');
          }

          return getOptionName(selectedValue);
        }}
        multiple={isMultipleSelect}
        readOnly={isReadOnly}
        sx={{ ...selectComponentStyles }}
      >
        {options.map((option) => {
          const rangeText = option.range ?? '';

          return (
            <MenuItem
              key={option.id}
              value={
                name === 'country' || name === 'preferredLanguage'
                  ? option.id
                  : option.name
              }
            >
              {isMultipleSelect ? (
                <>
                  <Checkbox
                    checked={
                      field.value
                        ? field.value.indexOf(option.name) > -1
                        : false
                    }
                  />
                  <ListItemText primary={option.name} />
                </>
              ) : (
                `${option.name} ${rangeText}`
              )}
            </MenuItem>
          );
        })}
      </Select>
      {showError && <FormHelperText error>{meta.error}</FormHelperText>}
      {meta.touched && !!alertMessage && (
        <FormHelperText> {alertMessage} </FormHelperText>
      )}
    </FormControl>
  );
};
